import { computed, inject } from '@angular/core';
import { patchState, signalStore, withComputed, withMethods, withState } from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { ToastrService } from 'ngx-toastr';
import { TranslateService } from '@ngx-translate/core';

import { of, pipe } from 'rxjs';
import { catchError, concatMap, finalize, map, switchMap, tap } from 'rxjs/operators';

import { ApplicationService, BoxService, DialogService, PartnerService } from '../services';
import { BoxState, initialBoxState } from '../models/state/box.state';
import { BoxPageRequest, BoxUpdateRequest } from '../models';
import { QrCodeDialogComponent } from '../../pages/main/admin-cockpit/admin-boxes/qr-code-dialog/qr-code-dialog.component';

export const BoxStore = signalStore(
  withState<BoxState>(initialBoxState),
  withComputed(({ boxesStatistics }) => ({
    metrics: computed(() => [
      {
        label: 'boxes.metrics.totalBoxes',
        value: boxesStatistics().totalCount,
        icon: 'storage',
        iconSize: 1.375,
      },
      {
        label: 'boxes.metrics.connectedBoxes',
        value: boxesStatistics().connectedCount,
        icon: 'power_settings_new',
        iconSize: 1.375,
      },
      {
        label: 'boxes.metrics.disconnectedBoxes',
        value: boxesStatistics().totalCount - boxesStatistics().connectedCount,
        icon: 'do_not_disturb_on',
        iconSize: 1.375,
      },
    ]),
  })),
  withMethods((store, applicationService = inject(ApplicationService), boxService = inject(BoxService)) => ({
    loadBoxes: rxMethod<BoxPageRequest>(
      pipe(
        map((_pageRequest) => {
          const pageRequest = { ..._pageRequest, page: 0, size: applicationService.itemsPerPage() };
          patchState(store, { loading: true, pageRequest });
          return pageRequest;
        }),
        switchMap((pageRequest) =>
          boxService.getBoxesByRole(pageRequest).pipe(
            tap((boxes) => patchState(store, { boxes })),
            catchError(() => of()),
            finalize(() => patchState(store, { loading: false })),
          ),
        ),
      ),
    ),
    loadMoreBoxes: rxMethod<void>(
      pipe(
        map(() => {
          const { page, ...restOfPageRequest } = store.pageRequest();
          const pageRequest = { ...restOfPageRequest, page: (page ?? 0) + 1 };
          patchState(store, { pageRequest });
          return pageRequest;
        }),
        concatMap((pageRequest) =>
          boxService.getBoxesByRole(pageRequest).pipe(
            tap((boxes) => patchState(store, { boxes: [...store.boxes(), ...boxes] })),
            catchError(() => of()),
          ),
        ),
      ),
    ),
    loadBoxesStatistics: rxMethod<void>(
      pipe(
        tap(() => patchState(store, { statisticsLoading: true })),
        switchMap(() =>
          boxService.getBoxesStatisticsByRole().pipe(
            tap((boxesStatistics) => patchState(store, { boxesStatistics })),
            catchError(() => of()),
            finalize(() => patchState(store, { statisticsLoading: false })),
          ),
        ),
      ),
    ),
  })),
  withMethods(
    (
      store,
      boxService = inject(BoxService),
      partnerService = inject(PartnerService),
      toastrService = inject(ToastrService),
      translateService = inject(TranslateService),
      dialogService = inject(DialogService),
    ) => ({
      updateBox: rxMethod<{ id: string; boxData: BoxUpdateRequest }>(
        pipe(
          switchMap(({ id, boxData }) =>
            boxService.updateBoxByRole(id, boxData).pipe(
              tap((box) => {
                patchState(store, {
                  boxes: [...store.boxes().map((b) => (b.id === id ? { ...b, ...box } : b))],
                });
                toastrService.success(translateService.instant('boxes.alerts.updateSuccess'));
              }),
              catchError(() => {
                toastrService.error(translateService.instant('boxes.alerts.updateFail'));
                return of();
              }),
            ),
          ),
        ),
      ),
      deleteBox: rxMethod<string>(
        pipe(
          switchMap((id) =>
            boxService.deleteBoxByRole(id).pipe(
              tap(() => {
                patchState(store, {
                  boxes: store.boxes().filter((b) => b.id !== id),
                });
                toastrService.success(translateService.instant('boxes.alerts.deleteSuccess'));
                store.loadBoxesStatistics();
              }),
              catchError(() => {
                toastrService.error(translateService.instant('boxes.alerts.deleteFail'));
                return of();
              }),
            ),
          ),
        ),
      ),
      loadManagers: rxMethod<void>(
        switchMap(() =>
          partnerService.getAllPartners().pipe(
            tap((managers) => patchState(store, { managers })),
            catchError(() => of()),
          ),
        ),
      ),
      generateQrCode: rxMethod<string>(
        switchMap((boxId) =>
          boxService.generateQRCode({ boxId }).pipe(
            tap((qrCode) => dialogService.openDialog(QrCodeDialogComponent, qrCode)),
            catchError(() => {
              toastrService.error(translateService.instant('boxes.alerts.generateQrCodeFail'));
              return of();
            }),
          ),
        ),
      ),
    }),
  ),
);
